import { Box, Flex, Grid, Heading, Skeleton, Text } from '@chakra-ui/react';
import React, { useContext, useMemo, useState, createContext } from 'react';

import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { logout } from '~/contexts/UserAuthProvider';
import { Icon } from '~/packages/common-frontend/components/icons';

type CreditFacilityContextType = {
  creditFacilityId: string;
  setCreditFacilityId: (nextId: string) => void;
};
const CreditFacilityContext = createContext<CreditFacilityContextType | null>(
  null,
);

export const useCreditFacilityContext = () => {
  const context = useContext(CreditFacilityContext);
  if (context === null) {
    throw new Error('CreditFacilityContext must be present');
  }

  return context;
};

// Fragments
type LogOutButtonProps = {
  iconOnly?: boolean;
  disableOnClick?: boolean;
};
const LogOutButton = ({ iconOnly, disableOnClick }: LogOutButtonProps) => {
  return (
    <Flex
      alignItems="center"
      onClick={disableOnClick ? undefined : () => logout()}
      cursor={disableOnClick ? undefined : 'pointer'}
    >
      {iconOnly && <Icon icon="arrow-right-from-bracket" size="medium" />}

      {!iconOnly && (
        <Flex alignItems="center" pl="10px">
          <Grid mr="12px" height={8} placeItems="center">
            <Icon icon="arrow-right-from-bracket" size="medium" />
          </Grid>
          <Text className="side-bar-item" fontSize="14px">
            Log Out
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

const CreditFacilityMissingFallback = () => (
  <Box
    padding={{ base: '30px', lg: '50px 40px' }}
    margin="0 auto"
    width="100%"
    maxWidth="1600px"
  >
    <Box>
      <Flex justify="space-between" width="100%">
        <Heading marginBottom="70px" display="inline-block">
          No credit facilities configured.
        </Heading>
        <Box width="30%">
          <Flex justify="space-between" alignItems="center">
            <LogOutButton />
          </Flex>
        </Box>
      </Flex>
    </Box>
  </Box>
);

// Exports
type CreditFacilityProviderProps = {
  children: React.ReactNode;
  renderLoading?: React.ReactElement;
  fallbackComponent?: React.ReactElement;
};
export const CreditFacilityProvider = ({
  children,
  renderLoading = <Skeleton height="50vh" width="100%" margin="1.5rem" />,
  fallbackComponent = <CreditFacilityMissingFallback />,
}: CreditFacilityProviderProps) => {
  const { facilities } = useCreditFacilitiesOrThrow();
  const [creditFacilityId, setCreditFacilityId] = useState<string>();

  const context = useMemo(() => {
    if (facilities.length === 0) return null;

    if (
      !creditFacilityId ||
      !facilities.find((facility) => facility.id === creditFacilityId)
    ) {
      const firstFacilityId = facilities[0]?.id as string | undefined; // array index lookup can be undefined
      if (!firstFacilityId) return null;
      return {
        creditFacilityId: firstFacilityId,
        setCreditFacilityId,
      };
    }

    return { creditFacilityId, setCreditFacilityId };
  }, [creditFacilityId, setCreditFacilityId, facilities]);

  if (facilities.length === 0) return fallbackComponent;
  if (!context) return renderLoading;

  return (
    <CreditFacilityContext.Provider value={context}>
      {children}
    </CreditFacilityContext.Provider>
  );
};
